import React, { useEffect, useMemo, useState } from 'react';
import AlertBanner from '../../common/AlertBanner/AlertBanner';
import Button from '../../common/Button/Button';
import ConfirmModal from '../../common/ConfirmModal/ConfirmModal';
import FormInput from '../../common/FormInput/FormInput';
import Tooltip from '../../common/Tooltip';
import ViewLinkButton from '../../common/ViewLinkButton/ViewLinkButton';
import { StudentService } from '../../../services';
import type { StudentConsultancyCreditState } from '../../../types';
import { formatDate } from '../../../utils';
import { getDefaultPausedUntilDate, getTodayBrtDateOnly } from '../../../utils/subscriptionPause';
import styles from './StudentConsultancyCreditControl.module.css';

const EMPTY_CREDIT: StudentConsultancyCreditState = {
  hasCredit: false,
  credit: null,
};

interface StudentConsultancyCreditControlProps {
  studentId: number;
  canManage: boolean;
  variant?: 'inline' | 'headerIcon';
  iconButtonClassName?: string;
  onChanged?: () => void;
}

const StudentConsultancyCreditControl: React.FC<StudentConsultancyCreditControlProps> = ({
  studentId,
  canManage,
  variant = 'inline',
  iconButtonClassName,
  onChanged,
}) => {
  const [state, setState] = useState<StudentConsultancyCreditState>(EMPTY_CREDIT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [pausedUntil, setPausedUntil] = useState('');

  const today = useMemo(getTodayBrtDateOnly, [showGrantModal]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const nextState = await StudentService.getConsultancyCredit(studentId);
        if (!cancelled) {
          setState(nextState);
        }
      } catch (loadError) {
        if (!cancelled) {
          setState(EMPTY_CREDIT);
          setError(loadError instanceof Error ? loadError.message : 'Erro ao carregar o crédito da aluna.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const handleGrant = async () => {
    if (!pausedUntil) {
      setError('Informe até quando as cobranças devem ficar pausadas.');
      return;
    }
    if (pausedUntil < today) {
      setError('A data até quando as cobranças ficam pausadas não pode ser anterior a hoje.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      setState(await StudentService.grantConsultancyCredit(studentId, pausedUntil));
      setShowGrantModal(false);
      onChanged?.();
    } catch (grantError) {
      setError(grantError instanceof Error ? grantError.message : 'Erro ao iniciar o período de crédito.');
    } finally {
      setSaving(false);
    }
  };

  const handleUndo = async () => {
    setSaving(true);
    setError(null);
    try {
      setState(await StudentService.undoConsultancyCredit(studentId));
      setShowUndoModal(false);
      onChanged?.();
    } catch (undoError) {
      setError(undoError instanceof Error ? undoError.message : 'Erro ao desfazer o crédito.');
    } finally {
      setSaving(false);
    }
  };

  const validUntilLabel = state.credit?.validUntil ? formatDate(state.credit.validUntil) : null;
  const isHeader = variant === 'headerIcon';
  const statusText = !loading && state.hasCredit && validUntilLabel
    ? `Crédito até ${validUntilLabel}`
    : null;

  const openGrantModal = () => {
    setError(null);
    setPausedUntil(getDefaultPausedUntilDate());
    setShowGrantModal(true);
  };

  const openUndoModal = () => {
    setError(null);
    setShowUndoModal(true);
  };

  const actionButton = canManage && !loading
    ? (
      state.hasCredit ? (
        isHeader ? (
          <Tooltip content="Desfazer crédito">
            <Button
              type="button"
              variant="link"
              size="xs"
              icon="undo"
              iconSize={16}
              className={iconButtonClassName}
              aria-label="Desfazer crédito"
              disabled={saving}
              onClick={openUndoModal}
            />
          </Tooltip>
        ) : (
          <ViewLinkButton
            label="Desfazer crédito"
            icon={null}
            disabled={saving}
            onClick={openUndoModal}
          />
        )
      ) : (
        isHeader ? (
          <Tooltip content="Iniciar período de crédito">
            <Button
              type="button"
              variant="link"
              size="xs"
              icon="payments"
              iconSize={16}
              className={iconButtonClassName}
              aria-label="Iniciar período de crédito"
              disabled={saving}
              onClick={openGrantModal}
            />
          </Tooltip>
        ) : (
          <ViewLinkButton
            label="Iniciar período de crédito"
            icon={null}
            disabled={saving}
            onClick={openGrantModal}
          />
        )
      )
    )
    : null;

  const modals = (
    <>
      <ConfirmModal
        open={showGrantModal}
        title="Iniciar período de crédito"
        message="Informe até quando o crédito vale. Nesse período, cobranças que surgirem são canceladas no gateway e ficam no histórico do portal como Crédito."
        confirmLabel="Iniciar período de crédito"
        cancelLabel="Voltar"
        variant="info"
        icon="payments"
        loading={saving}
        alignment="left"
        width={480}
        onConfirm={() => {
          void handleGrant();
        }}
        onCancel={() => {
          if (!saving) {
            setShowGrantModal(false);
            setError(null);
          }
        }}
      >
        <FormInput
          id="consultancy-credit-paused-until"
          label="Crédito válido até"
          type="date"
          required
          value={pausedUntil}
          min={today}
          disabled={saving}
          onChange={(event) => {
            setPausedUntil(event.target.value);
            if (error) setError(null);
          }}
          error={error && showGrantModal ? error : undefined}
        />
      </ConfirmModal>

      <ConfirmModal
        open={showUndoModal}
        title="Desfazer crédito"
        message={error && showUndoModal
          ? error
          : 'A aluna deixará de ter crédito na consultoria e as cobranças da assinatura voltam a ser geradas no gateway.'}
        confirmLabel="Desfazer"
        cancelLabel="Voltar"
        variant="warning"
        icon="undo"
        loading={saving}
        onConfirm={() => {
          void handleUndo();
        }}
        onCancel={() => {
          if (!saving) {
            setShowUndoModal(false);
            setError(null);
          }
        }}
      />
    </>
  );

  if (isHeader) {
    return (
      <>
        {actionButton}
        {statusText && (
          <span className={styles['header-status']} title={statusText}>
            {statusText}
          </span>
        )}
        {modals}
      </>
    );
  }

  return (
    <div className={styles.wrap}>
      {error && !showGrantModal && !showUndoModal && (
        <AlertBanner message={error} variant="error" className={styles.feedback} />
      )}
      {statusText && (
        <p className={styles.status}>{statusText}</p>
      )}
      {actionButton}
      {modals}
    </div>
  );
};

export default StudentConsultancyCreditControl;
